/* eslint-disable */

import { useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../Components/ui/card'
import { Button } from '../../Components/ui/button'
import { Label } from '../../Components/ui/label'
import { Input } from '../../Components/ui/input'
import { RadioGroup, RadioGroupItem } from '../../Components/ui/radio-group'
import { post } from '../../http/restservice'
import env from '../../env/env'
import { ChevronDown, ChevronLeft } from 'lucide-react'
import { useModal } from '../../hooks/use-modal-store'
import { useRecoilValueLoadable } from 'recoil'
import { MFTimeBasedBaskets } from '../../store/MFBasketsStore'
import { toCurrency } from '../../utils/CurrencyUtil'
import useDebounce from '../../hooks/useDebounce'
import { BASKET_SUBSEQUENT_ONE_TIME_MIN } from '../../constants/oneTime'

const InvestMoreOneTime = () => {
  const location = useLocation()

  const navigate = useNavigate()
  const [amount, setAmount] = useState(Number(location?.state?.payload?.orderInvestmentAmount) || 0)
  const [amountInput, setAmountInput] = useState('0')

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const { onOpen } = useModal()

  const baskets = useRecoilValueLoadable(MFTimeBasedBaskets)

  const [dynamicBreakdown, setDynamicBreakdown] = useState([])
  const [selectedBreakdownType, setSelectedBreakdownType] = useState('dynamic')
  const debouncedAmount = useDebounce(amount, 500)

  useEffect(() => {
    setAmountInput(toCurrency(Number(amount || 0)))
  }, [amount])

  useEffect(() => {
    getDynamicAllocation(debouncedAmount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAmount])

  const handleAmountInputChange = (e) => {
    const raw = (e?.target?.value || '').toString().replace(/[^0-9]/g, '')
    const numeric = raw ? Number(raw) : 0
    setAmount(numeric)
    setAmountInput(toCurrency(numeric))
  }



  const universalBasketId =
    +location?.state?.basket[0]?.universalBasketId || +location?.state?.basket?.universalBasketId

  const basketMinAmount = BASKET_SUBSEQUENT_ONE_TIME_MIN[universalBasketId]

  const getNewOneTimeBreakdown = () => {
    const basketFunds =
      baskets?.state === 'hasValue'
        ? baskets?.contents?.data?.filter((plan) => plan.universalBasketId === universalBasketId) ||
        []
        : []



    if (amount < basketMinAmount) {
      const fallbackFund = basketFunds?.find(
        (fund) => fund?.bseSchemeCode?.toLowerCase() === 'cmfcgp-gr',
      )

      return fallbackFund
        ? [
          {
            ...fallbackFund,
            weightage: 100,
            allocationAmount: amount,
            investmentAmount: amount,
          },
        ]
        : []
    }

    const filteredBasketFunds = basketFunds?.filter((fund) => fund.weightageOneTime
      > 0)
      .map((fund) => ({
        ...fund,
        investmentAmount: (amount / 100) * fund.weightageOneTime,
        allocationAmount: (amount / 100) * fund.weightageOneTime,
      }))

    return filteredBasketFunds
  }


  const getDynamicAllocation = async (investmentAmount) => {
    if (!investmentAmount || investmentAmount <= 0) {
      setDynamicBreakdown([])
      return
    }

    const response = await post("mutualfundservice/api/basket/calculateDynamicSubsequentInvestmentBreakdown",

      {
        "customerBasketInvestmentId": location?.state?.payload?.[0]?.customerBasketInvestmentId || location?.state?.basket?.[0]?.customerBasketInvestmentId || location?.state?.basket?.customerBasketInvestmentId,
        "universalBasketId": +location?.state?.payload?.[0]?.universalBasketId || +location?.state?.basket?.[0]?.universalBasketId || +location?.state?.basket?.universalBasketId,
        "subsequentInvestmentAmount": investmentAmount
      }
    )

    if (response?.isError) {

      onOpen('openAdminPopup', {
        title: 'Unable to Fetch Dynamic Allocation',
        message:
          'We were unable to retrieve the dynamic allocation details at this time. Please try again later. If the issue persists, contact support.',
        severity: 'error',
      })
    }

    if (!response?.isError && response?.status === "SUCCESS") {
      setDynamicBreakdown(response?.distributionData)
    }

  }

  const handleSubmit = async () => {
    setError('')

    // Basic validation
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      setError('Please enter a valid investment amount')
      return
    }

    const url = `${env.API_URL}adminservice/api/actionPlan/subsequentOneTimeOrder`

    const payload = {
      clientCode: location?.state?.clientCode,
      orderInvestmentAmount: amount,
      orderInvestmentBreakdown: selectedBreakdownType === 'dynamic' ? dynamicBreakdown?.map((fund) => ({
        ...fund,
        investmentAmount: fund?.allocationAmount
      }))?.filter((f) => f.allocationAmount > 0) : getNewOneTimeBreakdown(),
      customerBasketInvestmentId: location?.state?.payload?.[0]?.customerBasketInvestmentId || location?.state?.basket?.[0]?.customerBasketInvestmentId || location?.state?.basket?.customerBasketInvestmentId,
    }

    setIsLoading(true)


    const response = await post(url, payload)

    setIsLoading(false)

    if (
      response?.status === 'SUCCESS' &&
      response?.message?.includes('Order created successfully')
    ) {
      onOpen('openAdminPopup', {
        title: 'Order Created!',
        message: 'Your order has been successfully created.',
        severity: 'success',
      })
    } else {
      onOpen('openAdminPopup', {
        title: 'Order Failed!',
        message:
          'There was an issue creating your order. Please try again later or contact support if the issue persists.',
        severity: 'error',
      })
    }
  }

  return (
    <>
      <ChevronLeft onClick={() => navigate(-1)} className='cursor-pointer' />
      <div className='container flex items-center justify-center min-h-[70vh] py-12'>
        <Card className='w-full max-w-xl'>
          <CardHeader>
            <CardTitle className='text-2xl font-bold'>
              {location?.state?.basket[0]?.customerBasketName ||
                location?.state?.basket?.customerBasketName}{' '}
              - (One-Time)
            </CardTitle>
            <CardDescription>
              Enter the amount you would like to invest in your portfolio
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='amount' className='font-semibold'>
                  One-Time Investment Amount
                </Label>
                <div className='relative'>
                  <Input
                    startAdornment='₹'
                    id='amount'
                    type='text'
                    placeholder='0'
                    className='pl-10'
                    value={amountInput}
                    onChange={handleAmountInputChange}
                  />
                </div>
                {error && <p className='text-sm font-medium text-red-500'>{error}</p>}
              </div>

              <div className='flex flex-col items-center gap-3'>
                <Button
                  variant='link'
                  size='simple'
                  className='text-primary-500 p-0 border-b border-dashed border-primary-500 rounded-none hover:no-underline'
                  onClick={(e) => {
                    e.stopPropagation(),

                      onOpen('OpenAdminMFBreakdown', {
                        constituents: getNewOneTimeBreakdown(),
                        staticConstituents: getNewOneTimeBreakdown(),
                        dynamicConstituents:
                          dynamicBreakdown?.length > 0
                            ? dynamicBreakdown?.filter((f) => f.allocationAmount > 0)
                            : getNewOneTimeBreakdown(),
                        hasDualBreakdown: true,
                        amount: amount,
                      })
                  }}
                >
                  View detailed breakdown
                  <ChevronDown className='h-4 w-4 ml-1' />
                </Button>

                <div className='w-full space-x-2 flex justify-center items-center'>
                  <Label className='text-sm font-medium text-black-400'>
                    Please select one to continue
                  </Label>
                  <RadioGroup
                    value={selectedBreakdownType}
                    onValueChange={setSelectedBreakdownType}
                    className='flex flex-row gap-6'
                  >
                    <div className='flex items-center space-x-2'>
                      <RadioGroupItem value='dynamic' id='breakdown-dynamic' />
                      <Label htmlFor='breakdown-dynamic' className='font-medium cursor-pointer'>
                        Dynamic
                      </Label>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <RadioGroupItem value='static' id='breakdown-static' />
                      <Label htmlFor='breakdown-static' className='font-medium cursor-pointer'>
                        Static
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              {/* {amount < minimumInvestmentAmount && (
                <div>
                  <p className='text-sm text-error-500 text-center'>
                    Minimum investment amount is {toCurrency(minimumInvestmentAmount)}
                  </p>
                </div>
              )} */}




            </div>
          </CardContent>

          <CardFooter>
            <Button
              onClick={handleSubmit}
              type='submit'
              className='w-full bg-primary-500 disabled:bg-primary-300'
              disabled={isLoading || amount == 0}
            >
              {isLoading ? 'Processing...' : 'Continue'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </>
  )
}

export default InvestMoreOneTime
